import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, Download, Users, UserCheck, UserX, UserPlus, Search } from 'lucide-react'
import { clientService } from '@services/client.service'
import { Client } from '@/types'
import { cn } from '@lib/utils'

type TabType = 'all' | 'responders' | 'non-responders' | 'new'

/** Fetch all clients by paginating through pages of 100 */
function useAllClients() {
  return useQuery({
    queryKey: ['clients', 'all-for-tracking'],
    queryFn: async () => {
      const firstPage = await clientService.fetchClients({ limit: 100, page: 1 })
      const allClients = [...firstPage.data]
      const totalPages = firstPage.pagination.totalPages

      if (totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            clientService.fetchClients({ limit: 100, page: i + 2 })
          )
        )
        for (const page of remaining) {
          allClients.push(...page.data)
        }
      }

      return allClients
    },
  })
}

export function TrackingPage() {
  const [tab, setTab] = useState<TabType>('all')
  const [search, setSearch] = useState('')

  const { data: allClients = [], isLoading } = useAllClients()

  const { responders, nonResponders, newClients } = useMemo(() => {
    const responders: Client[] = []
    const nonResponders: Client[] = []
    const newClients: Client[] = []

    for (const client of allClients) {
      if (client.email.includes('@veles.digital')) continue

      // Parse customData - may come as string from API or as object
      let customData: Record<string, unknown> | null = null
      if (client.customData) {
        if (typeof client.customData === 'string') {
          try { customData = JSON.parse(client.customData) } catch { customData = null }
        } else {
          customData = client.customData as Record<string, unknown>
        }
      }

      if (client.status === 'RESPONDED') {
        responders.push(client)
      } else if (customData?.nonResponder === true) {
        nonResponders.push(client)
      } else if (
        client.status === 'PENDING' ||
        client.status === 'EMAIL_1_SENT' ||
        client.status === 'EMAIL_2_SENT' ||
        client.status === 'EMAIL_3_SENT'
      ) {
        newClients.push(client)
      }
    }

    return { responders, nonResponders, newClients }
  }, [allClients])

  const displayedClients = useMemo(() => {
    let list: Client[]
    switch (tab) {
      case 'responders': list = responders; break
      case 'non-responders': list = nonResponders; break
      case 'new': list = newClients; break
      default: list = allClients.filter(c => !c.email.includes('@veles.digital'))
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(c =>
        c.email.toLowerCase().includes(q) ||
        (c.fullName ?? '').toLowerCase().includes(q) ||
        (c.company ?? '').toLowerCase().includes(q)
      )
    }
    return list
  }, [tab, allClients, responders, nonResponders, newClients, search])

  const exportCsv = (clients: Client[], filename: string) => {
    const headers = ['Email', 'Full Name', 'Company', 'Phone', 'Status', 'Import Batch']
    const rows = clients.map(c => {
      let customData: Record<string, unknown> | null = null
      if (c.customData) {
        customData = typeof c.customData === 'string' ? (() => { try { return JSON.parse(c.customData as string) } catch { return null } })() : c.customData as Record<string, unknown>
      }
      const phone = (customData?.phone as string) ?? (c as any).phone ?? ''
      return [
        c.email,
        c.fullName ?? '',
        c.company ?? '',
        phone,
        c.status,
        c.importBatch ?? '',
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 size={32} className="text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Tracking</h1>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryCard label="Responders" value={responders.length} icon={<UserCheck size={20} />} color="green" loading={isLoading} />
        <SummaryCard label="Non-responders" value={nonResponders.length} icon={<UserX size={20} />} color="red" loading={isLoading} />
        <SummaryCard label="New / Active" value={newClients.length} icon={<UserPlus size={20} />} color="blue" loading={isLoading} />
        <SummaryCard label="Total Tracked" value={allClients.filter(c => !c.email.includes('@veles.digital')).length} icon={<Users size={20} />} color="gray" loading={isLoading} />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name, email, company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-input bg-background py-2 pl-10 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {/* Tabs + Export */}
      <div className="flex items-center justify-between border-b border-border pb-2">
        <div className="flex gap-2">
          {([
            ['all', 'All'],
            ['responders', 'Responders'],
            ['non-responders', 'Non-responders'],
            ['new', 'New (Round 2)'],
          ] as [TabType, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                tab === key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => exportCsv(displayedClients, `tracking-${tab}-${new Date().toISOString().slice(0, 10)}`)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          disabled={isLoading}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">Loading clients...</div>
      ) : displayedClients.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No clients in this category</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Full Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Batch</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {displayedClients.map((client) => (
                <ClientRow key={client.id} client={client} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-sm text-muted-foreground">
        Showing {displayedClients.length} clients
      </div>
    </div>
  )
}

function SummaryCard({ label, value, icon, color, loading }: {
  label: string
  value: number
  icon: React.ReactNode
  color: 'green' | 'red' | 'blue' | 'gray'
  loading: boolean
}) {
  const colorClasses = {
    green: 'bg-green-900/30 text-green-300 border-green-800/50',
    red: 'bg-red-900/30 text-red-300 border-red-800/50',
    blue: 'bg-blue-900/30 text-blue-300 border-blue-800/50',
    gray: 'bg-muted text-muted-foreground border-border',
  }

  return (
    <div className={cn('rounded-lg border p-4', colorClasses[color])}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {icon}
      </div>
      <p className="mt-2 text-3xl font-bold">
        {loading ? '...' : value}
      </p>
    </div>
  )
}

const statusColors: Record<string, string> = {
  PENDING: 'bg-muted text-muted-foreground',
  EMAIL_1_SENT: 'bg-blue-900/40 text-blue-300',
  EMAIL_2_SENT: 'bg-blue-900/40 text-blue-300',
  EMAIL_3_SENT: 'bg-blue-900/40 text-blue-300',
  RESPONDED: 'bg-green-900/40 text-green-300',
  UNSUBSCRIBED: 'bg-orange-900/40 text-orange-300',
  BOUNCED: 'bg-red-900/40 text-red-300',
  FAILED: 'bg-red-900/40 text-red-300',
}

function ClientRow({ client }: { client: Client }) {
  let customData: Record<string, unknown> | null = null
  if (client.customData) {
    customData = typeof client.customData === 'string' ? (() => { try { return JSON.parse(client.customData as string) } catch { return null } })() : client.customData as Record<string, unknown>
  }
  const phone = (customData?.phone as string) ?? (client as any).phone ?? ''

  return (
    <tr className="hover:bg-accent/50">
      <td className="px-4 py-3 text-sm text-foreground">{client.email}</td>
      <td className="px-4 py-3 text-sm text-secondary-foreground">{client.fullName ?? '\u2014'}</td>
      <td className="px-4 py-3 text-sm text-secondary-foreground">{client.company ?? '\u2014'}</td>
      <td className="px-4 py-3 text-sm text-muted-foreground">{phone || '\u2014'}</td>
      <td className="px-4 py-3">
        <span className={cn(
          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
          statusColors[client.status] ?? 'bg-muted text-muted-foreground'
        )}>
          {client.status.replace(/_/g, ' ')}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">{client.importBatch ?? '\u2014'}</td>
    </tr>
  )
}
